import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OnboardingLayout from '../components/OnboardingLayout';
import VerifyEmailBanner from '../components/VerifyEmailBanner';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MIN = 500;
const MAX = 2500;
const STEP = 50;

const OnboardingBudget = () => {
  const navigate = useNavigate();
  const { user, loading, updateOnboarding } = useAuth();
  const [value, setValue] = useState(user?.monthly_budget_cad || 1000);
  const [saving, setSaving] = useState(false);

  if (loading) return null;
  if (!user) {
    navigate('/signup', { replace: true });
    return null;
  }

  const hint = user?.school_name
    ? `Most students near ${user.school_name} pay between $800 and $1,200 all-in.`
    : 'Most GTA students pay between $800 and $1,200 all-in.';

  const saveAndContinue = async () => {
    setSaving(true);
    try {
      await updateOnboarding({ monthly_budget_cad: value });
      navigate('/onboarding/place-type');
    } catch (e) {
      toast.error("We couldn't save that. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <VerifyEmailBanner />
      <OnboardingLayout
        step={3}
        title="What's your monthly housing budget?"
        subtitle="Drag the slider. This is your all-in budget — rent plus utilities, internet, and fees."
        testId="onboarding-budget"
      >
        <div className="text-center mb-8">
          <p className="text-5xl sm:text-6xl font-heading font-extrabold text-slate-900 tracking-tight" data-testid="budget-value">
            ${value.toLocaleString()}
            <span className="text-lg sm:text-xl font-semibold text-slate-500 ml-1">/month</span>
          </p>
        </div>

        <Slider
          min={MIN}
          max={MAX}
          step={STEP}
          value={[value]}
          onValueChange={(v) => setValue(v[0])}
          className="my-6"
          data-testid="budget-slider"
        />
        <div className="flex justify-between text-xs text-slate-500 mt-1 mb-6">
          <span>${MIN.toLocaleString()}</span>
          <span>${MAX.toLocaleString()}</span>
        </div>

        <p className="text-sm text-slate-600 bg-amber-50 border border-amber-100 rounded-xl p-4 leading-relaxed" data-testid="budget-hint">
          {hint}
        </p>

        <div className="mt-8 space-y-3">
          <Button
            onClick={saveAndContinue}
            disabled={saving}
            className="w-full h-12 btn-primary text-base"
            data-testid="budget-continue-btn"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue
          </Button>
          <button
            onClick={() => navigate('/onboarding/place-type')}
            className="w-full text-sm text-slate-600 hover:text-slate-900 py-2"
            data-testid="budget-skip-link"
          >
            Skip this step
          </button>
        </div>
      </OnboardingLayout>
    </>
  );
};

export default OnboardingBudget;
